/**
 * 机上調査の調査項目の既定 (色分け・集計・評価の列) の入力欄 (docs/27 §9 / §17)。
 * 候補は出力フォーマットの分類軸から動的に列挙する。空欄は自動。
 */
import {
  listSurveyAxes,
  type AiOutputFormatSchema,
  type StreetSurveyDisplayDef,
} from '@citizen-walk/shared-types';

export type StreetSurveyAxisDefaults = Pick<
  StreetSurveyDisplayDef,
  'legendAxisKey' | 'aggregateAxisKeys' | 'evaluationField'
>;

export interface StreetSurveyAxisDefaultsFormProps {
  schema: AiOutputFormatSchema | null;
  value: StreetSurveyAxisDefaults;
  onChange: (next: StreetSurveyAxisDefaults) => void;
  disabled?: boolean;
}

const selectClass =
  'mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white';

export function StreetSurveyAxisDefaultsForm({
  schema,
  value,
  onChange,
  disabled = false,
}: StreetSurveyAxisDefaultsFormProps) {
  const axes = listSurveyAxes(schema);
  if (axes.length === 0) {
    return (
      <p className="text-xs text-slate-400">
        分類の項目を持つ出力フォーマットを選ぶと、色分け・集計・評価の列を選べます。
      </p>
    );
  }
  // 保存値が候補に無い (出力フォーマットが違う) ときも選択状態を保つ。
  const renderOptions = (key: string | null | undefined) => {
    const opts = axes.map((a) => ({ key: a.codeField, label: a.label }));
    if (key && !axes.some((a) => a.codeField === key || a.key === key)) {
      opts.push({ key, label: `${key}（この出力フォーマットにありません）` });
    }
    return opts.map((o) => (
      <option key={o.key} value={o.key}>
        {o.label}
      </option>
    ));
  };
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <label className="block text-xs text-slate-600 min-w-0">
        色分けする調査項目（凡例）
        <select
          value={value.legendAxisKey ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, legendAxisKey: e.target.value || null })}
          className={selectClass}
          style={{ maxWidth: '100%' }}
        >
          <option value="">自動（役割分類 → 主軸）</option>
          {renderOptions(value.legendAxisKey)}
        </select>
      </label>
      <label className="block text-xs text-slate-600 min-w-0">
        集計する調査項目
        <select
          value={value.aggregateAxisKeys?.[0] ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, aggregateAxisKeys: e.target.value ? [e.target.value] : [] })
          }
          className={selectClass}
          style={{ maxWidth: '100%' }}
        >
          <option value="">自動（主軸）</option>
          {renderOptions(value.aggregateAxisKeys?.[0])}
        </select>
      </label>
      <label className="block text-xs text-slate-600 min-w-0">
        評価（ポジ / ネガ / 中立）の列
        <select
          value={value.evaluationField ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, evaluationField: e.target.value || null })}
          className={selectClass}
          style={{ maxWidth: '100%' }}
        >
          <option value="">自動検出</option>
          {renderOptions(value.evaluationField)}
        </select>
      </label>
    </div>
  );
}
